import { Button, Menu, MenuItem } from '@/src/components/Antd';
import { Dropdown } from 'antd';
import ConfigContainer from '@/src/components/SalesChannelComponents/SalesChannelConfigContainer';
import ContentGrid from '@/src/components/SalesChannelComponents/SalesChannelContentGrid';
import { ContentModuleGroupPreloadingWrapper } from '@/src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import { useHomepage } from '@/src/pages/SalesChannelContentEditor/SalesChannelEditorHomepageResponsive/hooks/useSalesChannelContentHomepage';
import { get } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ClickableConfigItem from 'src/components/SalesChannelComponents/ClickableConfigItem';
import ContentReverseIcon from 'src/constrains/ContentReverseIcon';
import ChangesMonitor from 'src/constrains/SalesChannelContent-Revamp/HomePageChangesMonitor';
import Wrapper from 'src/constrains/SalesChannelContentWrapperRevamp';
import { isPrimaryLanguage } from 'src/constrains/SalesChannelMultiLanguageSelect/slice';
import { useI18n } from 'src/hooks/useRouterChange';
import { seoSetting } from 'src/pages/SalesChannelContentEditor/SalesChannelEditorHomepageResponsive/constants';
import { i18nGet, getI18nFromGeneral } from 'src/tools/tools';
import { EnumPageType } from 'src/types/common';
import ModulesList from '../../../constrains/SalesChannelContent-Revamp/ModulesList';
import { contentRender } from './contentRender';
import { InfoCircleOutlined } from '@ant-design/icons';
import TranslationTip from 'src/constrains/SalesChannelAITranslation/TranslationTip';
import css from './css.module.less';
import RouteBlock from 'src/constrains/RouterBlock/RouterBlock';
import { Icon } from '@/src/tools/icon';

const Homepage = () => {
  const {
    pcForm,
    mobileForm,
    tabletForm,
    seoSettingForm,
    orderForm,
    salesChannel,
    dataLoading,
    data,
    selectedLanguage,
    selectedItem,
    handleOnSelect,
    handleOnAdd,
    handleOnDuplicate,
    moduleData,
    handleOnSubmit,
    handleOnDelete,
    handlePCFormValuesChange,
    handleCloneFromPrimaryLanguage,
    handleOnSectionReset,
    handleOnSectionCopy,
    handleOnDiscard,
    handleReload,
    handleOnPreview,
    onGoBack,
    hasModified,
    setHasModified,
    loaded,
    handleOnRouteChange,
    salesChannelMode,
    translationEnabled,
    handleChangeTranslation,
    translationLoading,
  } = useHomepage();
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const isOnPrimaryLanguage = useSelector(isPrimaryLanguage());
  const _params: any = useParams();
  const { contentUuid, saleChannelUuid } = _params;
  // const saleChannelUuid = get(salesChannel, 'uuid');
  const headerProps = {
    title: get(salesChannel, 'name'),
    description: get(data, 'salesChannelContent.versionName'),
    status: get(data, 'salesChannelContent.status'),
    hasModified,
    onSubmit: handleOnSubmit,
    onDiscard: handleOnDiscard,
    onGoBack,
    handleOnTranslate: handleChangeTranslation,
    translationEnabled,
    translationLoading,
    rarePart: !dataLoading ? (
      <Dropdown.Button
        className={css.dropdownButton}
        icon={<Icon type='icondown' className={css.icon} />}
        getPopupContainer={() => document.body}
        overlay={
          <Menu>
            <MenuItem>
              <ContentReverseIcon
                onlyText
                salesChannelUuid={saleChannelUuid}
                entityUuid={get(data, 'uuid')}
                locale={selectedLanguage}
                entityType={EnumPageType.ContentHomePage}
                onReload={({ qParams }) => {
                  return handleReload(qParams);
                }}
                disabled={translationEnabled}
              />
            </MenuItem>
            <MenuItem onClick={handleOnPreview}>
              {getI18n('salesChannelContent.homePage.previewChange')}
            </MenuItem>
          </Menu>
        }>
        <div className={css.dropdownLeft}>
          {getI18nFromGeneral('general.action.moreAction')}
        </div>
      </Dropdown.Button>
    ) : (
      <React.Fragment />
    ),
  };

  const displayProps = {
    pcForm,
    mobileForm,
    tabletForm,
    selectedItem,
    moduleData,
  };

  return (
    <ContentModuleGroupPreloadingWrapper
      saleChannelUuid={saleChannelUuid}
      contentUuid={contentUuid}>
      <ChangesMonitor
        loaded={loaded}
        setHasModified={setHasModified}
        translationEnable={translationEnabled}
        pcForm={pcForm}
        tabletForm={tabletForm}
        mobileForm={mobileForm}
        seoForm={seoSettingForm}
        orderForm={orderForm}
      />
      <RouteBlock
        onConfirm={(f) => {
          handleOnRouteChange();
          if (f) {
            f();
          }
        }}
        okText={getI18nFromGeneral('general.commonHeader.discard_text')}
        active={hasModified}
        title={getI18nFromGeneral('general.commonHeader.discard_header')}
        content={getI18nFromGeneral('general.commonHeader.discard_content')}
      />
      {dataLoading ? null : (
        <Wrapper headerProps={headerProps}>
          {translationEnabled && !isOnPrimaryLanguage && <TranslationTip />}
          <ContentGrid
            hasTranslationTip={translationEnabled && !isOnPrimaryLanguage}
            configCols={contentRender({
              pcForm,
              mobileForm,
              tabletForm,
              seoSettingForm,
              selectedItem,
              handlePCFormValuesChange,
              handleOnSectionReset,
              handleOnSectionCopy,
              salesChannelMode,
              disabled: translationEnabled,
            })}
            mainCol={
              <ConfigContainer
                offset={
                  isOnPrimaryLanguage ? 250 : translationEnabled ? 365 : 315
                }
                top={
                  <>
                    {!isOnPrimaryLanguage && (
                      <Button
                        block
                        type={'primary'}
                        size={'large'}
                        style={{
                          marginBottom: '9px',
                          textAlign: 'left',
                          padding: '10px 12px',
                          height: '56px',
                          fontWeight: 'bold',
                        }}
                        onClick={handleCloneFromPrimaryLanguage}
                        disabled={translationEnabled}>
                        {getI18n('general.cloneBtn.error_title')}
                      </Button>
                    )}
                    <ClickableConfigItem
                      isSelected={selectedItem === seoSetting}
                      content={getI18n(
                        'salesChannelContent.index.homepageSeoSetting',
                      )}
                      onClick={() => {
                        handleOnSelect(seoSetting);
                      }}
                    />
                  </>
                }
                bottom={
                  <React.Fragment>
                    <div className={css.titleContainer}>
                      <InfoCircleOutlined rev className={css.icon} />
                      <div>{getI18n('general.dragmodule')}</div>
                    </div>
                    <ModulesList
                      form={orderForm}
                      onClick={handleOnSelect}
                      onDelete={handleOnDelete}
                      onAdd={handleOnAdd}
                      onDuplicate={handleOnDuplicate}
                      selectedItem={selectedItem}
                      //
                      errorDisplay={displayProps}
                      visibleDisplay={displayProps}
                      disabled={translationEnabled}
                    />
                  </React.Fragment>
                }
              />
            }
          />
        </Wrapper>
      )}
    </ContentModuleGroupPreloadingWrapper>
  );
};
export default Homepage;
